import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  RefreshCw,
  Users,
  UserCheck,
  UserX,
  Clock3,
  Eye,
} from "lucide-react";
import Card from "../../components/common/Card";
import MainHeading from "../../components/common/MainHeading";
import CommonTable, {
  type TableColumn,
} from "../../components/common/CommonTable";
import CommonFilter from "../../components/common/CommonFilter";
import TablePagination from "../../components/common/TablePagination";
import { Button } from "../../components/ui/button";
import { ThrowError } from "../../api/callApi";
import { RoutesLink } from "../../utility/RouteLinks";
import type { IPageInfo } from "@/interface/common.interface";
import type {
  IAttendanceRecord,
  AttendanceApiStatus,
} from "@/interface/attendance.model";
import { AttendanceService } from "@/services/Attendance/Attendance";
import LaborAttendanceLegend, {
  LABOR_STATUS_OPTIONS,
  LABOR_STATUS_STYLE_MAP,
} from "./LaborAttendanceLegend";

// UI status ("PRESENT" / "ABSENT" / "HALF_DAY") <-> API status
// ("present" / "absent" / "halfday") — same mapping used in BulkAttendance.
const toUiStatus = (
  status: AttendanceApiStatus,
): "PRESENT" | "ABSENT" | "HALF_DAY" | "--" => {
  switch (status) {
    case "present":
      return "PRESENT";
    case "absent":
      return "ABSENT";
    case "halfday":
      return "HALF_DAY";
    default:
      return "--";
  }
};

const toApiStatusFilter = (
  uiStatus: string,
): Exclude<AttendanceApiStatus, "pending"> | undefined => {
  switch (uiStatus) {
    case "PRESENT":
      return "present";
    case "ABSENT":
      return "absent";
    case "HALF_DAY":
      return "halfday";
    default:
      return undefined;
  }
};

// ─── Status dot map ──────────────────────────────────────────────
const STATUS_DOT_MAP: Record<string, string> = {
  PRESENT: "bg-emerald-500",
  HALF_DAY: "bg-amber-500",
  ABSENT: "bg-red-500",
};

// Rounds a check-in/check-out pair into worked hours for display only.
// Handles overnight shifts (check-out time earlier than check-in time).
const computeHoursWorked = (
  checkInTime?: string,
  checkOutTime?: string,
): number | null => {
  if (!checkInTime || !checkOutTime) return null;
  const start = moment(checkInTime, "HH:mm");
  let end = moment(checkOutTime, "HH:mm");
  if (end.isBefore(start)) {
    end = end.add(1, "day");
  }
  const hours = end.diff(start, "minutes") / 60;
  return Math.round(hours * 100) / 100;
};

const DeviceAttendance: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [date, setDate] = useState(moment().format("YYYY-MM-DD"));
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [records, setRecords] = useState<IAttendanceRecord[]>([]);
  const [paginationData, setPaginationData] = useState<IPageInfo>({
    currentPage: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  });

  const fetchDeviceAttendance = async () => {
    setIsLoading(true);
    try {
      const response = await AttendanceService.getAttendanceList({
        pageIndex: paginationData.currentPage,
        pageSize: paginationData.pageSize,
        search,
        status: toApiStatusFilter(statusFilter),
        date,
        sort: "createdAt",
        order: "DESC",
      });

      setRecords(response.data ?? []);
      setPaginationData((prev) => ({
        ...prev,
        total: response.total ?? 0,
        totalPages: Math.max(
          1,
          Math.ceil((response.total ?? 0) / (response.pageSize || prev.pageSize)),
        ),
      }));
    } catch (error) {
      ThrowError(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDeviceAttendance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, search, statusFilter, paginationData.currentPage, paginationData.pageSize]);

  // Quick-glance counts — reflect only the records on the current page,
  // since the list endpoint is paginated server-side.
  const summary = useMemo(() => {
    let present = 0;
    let halfDay = 0;
    let absent = 0;
    records.forEach((r) => {
      const uiStatus = toUiStatus(r.status);
      if (uiStatus === "PRESENT") present += 1;
      if (uiStatus === "HALF_DAY") halfDay += 1;
      if (uiStatus === "ABSENT") absent += 1;
    });
    return { total: records.length, present, halfDay, absent };
  }, [records]);

  // ─── Columns ────────────────────────────────────────────────────
  const columns: TableColumn<IAttendanceRecord>[] = [
    {
      key: "sNo",
      header: "S.No",
      render: (_item, index) => (
        <span className="text-muted-foreground font-medium text-sm">
          {(paginationData.currentPage - 1) * paginationData.pageSize + index + 1}
        </span>
      ),
    },
    {
      key: "workerId",
      header: "Worker",
      render: (item) => (
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold shadow-sm">
            {item.workerId.charAt(0).toUpperCase()}
          </div>
          <span className="font-semibold text-gray-700">{item.workerId}</span>
        </div>
      ),
    },
    {
      key: "date",
      header: "Date",
      render: (item) => (
        <span className="text-gray-600">
          {moment(item.date).format("DD MMM YYYY")}
        </span>
      ),
    },
    {
      key: "checkInTime",
      header: "Punch In",
      render: (item) => (
        <span className={item.checkInTime ? "text-gray-700 font-medium" : "text-gray-300"}>
          {item.checkInTime ? moment(item.checkInTime, "HH:mm").format("h:mm A") : "--"}
        </span>
      ),
    },
    {
      key: "checkOutTime",
      header: "Punch Out",
      render: (item) => (
        <span className={item.checkOutTime ? "text-gray-700 font-medium" : "text-gray-300"}>
          {item.checkOutTime ? moment(item.checkOutTime, "HH:mm").format("h:mm A") : "--"}
        </span>
      ),
    },
    {
      key: "hoursWorked",
      header: "Hours Worked",
      render: (item) => {
        const hours = computeHoursWorked(item.checkInTime, item.checkOutTime);
        return hours ? (
          <div className="flex items-center gap-2">
            <div className="w-16 h-1.5 rounded-full bg-slate-100 overflow-hidden">
              <div
                className="h-full bg-primary rounded-full"
                style={{ width: `${Math.min((hours / 9) * 100, 100)}%` }}
              />
            </div>
            <span className="font-medium text-sm text-gray-700">{hours} hrs</span>
          </div>
        ) : (
          <span className="text-gray-300">--</span>
        );
      },
    },
    {
      key: "paidForThisDays",
      header: "Paid",
      render: (item) => (
        <span
          className={`inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-full border ${
            item.paidForThisDays
              ? "text-green-600 border-green-600 bg-green-50"
              : "text-gray-400 border-gray-200 bg-gray-50"
          }`}
        >
          {item.paidForThisDays ? "Paid" : "Unpaid"}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (item) => {
        const uiStatus = toUiStatus(item.status);
        const label =
          LABOR_STATUS_OPTIONS.find((o) => o.value === uiStatus)?.label ||
          uiStatus;
        return (
          <span
            className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-full border ${LABOR_STATUS_STYLE_MAP[uiStatus]}`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT_MAP[uiStatus] || "bg-gray-400"}`} />
            {label}
          </span>
        );
      },
    },
    {
      key: "actions",
      header: "",
      render: (item) => (
        <Button
          variant="ghost"
          className="h-8 w-8 p-0 text-gray-400 hover:text-primary"
          onClick={() => navigate(`${RoutesLink.attendanceDetail}/${item.id}`)}
          title="View details"
        >
          <Eye className="h-4 w-4" />
        </Button>
      ),
    },
  ];

  // ─── Render ──────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            className="h-9 w-9 p-0 hover:bg-muted"
            onClick={() => navigate(RoutesLink.labor)}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <MainHeading title="Device Attendance" />
            <p className="text-sm text-gray-400 -mt-0.5">
              Attendance records for {moment(date).format("D MMM YYYY")}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 pl-12 sm:pl-0">
          <Button
            variant="outline"
            className="gap-2 bg-white shadow-sm"
            onClick={fetchDeviceAttendance}
            disabled={isLoading}
          >
            <RefreshCw size={16} className={isLoading ? "animate-spin" : ""} />
            {isLoading ? "Refreshing..." : "Refresh"}
          </Button>
        </div>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white border border-slate-200/80 rounded-2xl p-4 flex items-center gap-3 shadow-sm">
          <div className="w-10 h-10 rounded-xl bg-slate-100 text-slate-500 flex items-center justify-center shrink-0">
            <Users className="h-5 w-5" />
          </div>
          <div>
            <p className="text-xl font-semibold text-gray-900 leading-none">{summary.total}</p>
            <p className="text-xs text-gray-400 mt-1">Records on page</p>
          </div>
        </div>
        <div className="bg-white border border-slate-200/80 rounded-2xl p-4 flex items-center gap-3 shadow-sm">
          <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
            <UserCheck className="h-5 w-5" />
          </div>
          <div>
            <p className="text-xl font-semibold text-gray-900 leading-none">{summary.present}</p>
            <p className="text-xs text-gray-400 mt-1">Present</p>
          </div>
        </div>
        <div className="bg-white border border-slate-200/80 rounded-2xl p-4 flex items-center gap-3 shadow-sm">
          <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
            <Clock3 className="h-5 w-5" />
          </div>
          <div>
            <p className="text-xl font-semibold text-gray-900 leading-none">{summary.halfDay}</p>
            <p className="text-xs text-gray-400 mt-1">Half day</p>
          </div>
        </div>
        <div className="bg-white border border-slate-200/80 rounded-2xl p-4 flex items-center gap-3 shadow-sm">
          <div className="w-10 h-10 rounded-xl bg-red-50 text-red-500 flex items-center justify-center shrink-0">
            <UserX className="h-5 w-5" />
          </div>
          <div>
            <p className="text-xl font-semibold text-gray-900 leading-none">{summary.absent}</p>
            <p className="text-xs text-gray-400 mt-1">Absent</p>
          </div>
        </div>
      </div>

      <Card className="p-6 space-y-5 shadow-sm border-slate-200/80 rounded-2xl bg-white">
        <LaborAttendanceLegend />

        <CommonFilter
          searchValue={search}
          onSearchChange={(v) => {
            setSearch(v);
            setPaginationData((prev) => ({ ...prev, currentPage: 1 }));
          }}
          searchPlaceholder="Search worker ID..."
          singleDate={date}
          onSingleDateChange={(val) => {
            setDate(val || moment().format("YYYY-MM-DD"));
            setPaginationData((prev) => ({ ...prev, currentPage: 1 }));
          }}
          value={{ statusFilter }}
          filterName="statusFilter"
          filterPlaceholder="All Status"
          filterOptions={LABOR_STATUS_OPTIONS.filter((opt) => opt.value !== "--").map((opt) => ({
            value: opt.value,
            label: opt.label,
          }))}
          onChange={(val) => {
            setStatusFilter(val);
            setPaginationData((prev) => ({ ...prev, currentPage: 1 }));
          }}
        />

        <CommonTable data={records} columns={columns} loading={isLoading} stickyColumns={1} />

        <TablePagination
          paginationData={paginationData}
          setPaginationData={setPaginationData}
          handleChangePageSize={(size) =>
            setPaginationData((prev) => ({ ...prev, pageSize: size, currentPage: 1 }))
          }
          isLoading={isLoading}
        />
      </Card>
    </div>
  );
};

export default DeviceAttendance;